import React, { useState, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import SharedLocationForm from '../vedic/forms/LocationForm';
import ErrorAlert from '../vedic/feedback/ErrorAlert';
import LoadingState from '../vedic/feedback/LoadingState';
import SharedScoreRing from '../vedic/results/ScoreRing';
import ScoreCard from '../vedic/results/ScoreCard';

/* ─── Animations ─── */
const fadeIn = keyframes`from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}`;

/* ─── Styled Components ─── */
const PageContainer = styled.div`max-width:1200px;margin:40px auto;padding:0 20px;`;
const PageTitle = styled.h1`font-size:2rem;text-align:center;margin-bottom:5px;color:${p => p.theme.colors.text.primary};`;
const PageSubtitle = styled.p`text-align:center;color:${p => p.theme.colors.text.secondary};margin-bottom:30px;font-size:0.95rem;`;

const TopGrid = styled.div`
  display:grid;grid-template-columns:1fr 1fr;gap:30px;
  @media(max-width:992px){grid-template-columns:1fr}
`;

const Card = styled.div`
  background:${p => p.theme.colors.surface};border-radius: ${p => p.theme.radius.md}px;box-shadow: ${p => p.theme.shadow.card};padding:30px;
  animation:${fadeIn} 0.3s ease;
`;

const CardTitle = styled.h2`font-size:1.3rem;margin-bottom:20px;color:${p => p.theme.colors.text.primary};text-align:center;`;

const FormGroup = styled.div`margin-bottom:16px;`;
const Label = styled.label`display:block;margin-bottom:4px;font-weight:600;font-size:0.9rem;color:${p => p.theme.colors.text.secondary};`;
const Input = styled.input`
  width:100%;padding:10px 12px;border:1px solid ${p => p.theme.colors.border};border-radius:6px;font-size:0.95rem;box-sizing:border-box;
  &:focus{outline:none;border-color:${p => p.theme.colors.primary};box-shadow:0 0 0 2px rgba(108,99,255,0.15)}
`;
const Select = styled.select`
  width:100%;padding:10px 12px;border:1px solid ${p => p.theme.colors.border};border-radius:6px;font-size:0.95rem;
  &:focus{outline:none;border-color:${p => p.theme.colors.primary}}
`;
const RowGrid = styled.div`display:grid;grid-template-columns:${p => p.cols || '1fr 1fr'};gap:12px;
  @media(max-width:576px){grid-template-columns:1fr}
`;



/* ─── Component Scores ─── */
const ComponentGrid = styled.div`
  display:grid;grid-template-columns:repeat(5,1fr);gap:10px;margin:20px 0;
  @media(max-width:768px){grid-template-columns:repeat(3,1fr)}
  @media(max-width:480px){grid-template-columns:repeat(2,1fr)}
`;

/* ─── Panchanga Details ─── */
const PanchangaGrid = styled.div`
  display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-top:16px;
  @media(max-width:576px){grid-template-columns:1fr}
`;
const PanchangaCard = styled.div`
  background:${p => p.theme.colors.background};border-radius: ${p => p.theme.radius.md}px;padding:16px;border-left:3px solid ${p => p.theme.colors.primary};
`;
const PanchangaLabel = styled.div`font-size:0.75rem;font-weight:700;text-transform:uppercase;color:${p => p.theme.colors.text.muted};letter-spacing:0.5px;`;
const PanchangaValue = styled.div`font-size:1rem;font-weight:600;color:${p => p.theme.colors.text.primary};margin-top:4px;`;
const PanchangaNote = styled.div`font-size:0.8rem;color:${p => p.theme.colors.text.secondary};margin-top:2px;`;

/* ─── Warnings ─── */
const WarningCard = styled.div`
  background:#fff8e1;border-left:4px solid ${p => p.theme.colors.bands.weak};padding:14px;border-radius:0 8px 8px 0;
  margin-bottom:10px;animation:${fadeIn} 0.3s ease;
`;
const DoshaCard = styled.div`
  background:#fce4ec;border-left:4px solid ${p => p.theme.colors.bands.challenging};padding:14px;border-radius:0 8px 8px 0;
  margin-bottom:10px;animation:${fadeIn} 0.3s ease;
`;
const WarningText = styled.div`font-size:0.9rem;color:${p => p.theme.colors.text.secondary};`;

const BackButton = styled.button`
  background:none;border:1px solid ${p => p.theme.colors.border};border-radius:6px;padding:8px 20px;
  font-size:0.85rem;cursor:pointer;color:${p => p.theme.colors.text.secondary};margin-bottom:20px;
  &:hover{border-color:${p => p.theme.colors.primary};color:${p => p.theme.colors.primary}}
`;

const eventTypes = [
  { value: 'marriage', label: 'Marriage', ta: 'திருமணம்' },
  { value: 'business', label: 'Business', ta: 'வணிகம்' },
  { value: 'travel', label: 'Travel', ta: 'பயணம்' },
  { value: 'surgery', label: 'Surgery', ta: 'அறுவை சிகிச்சை' },
  { value: 'education', label: 'Education', ta: 'கல்வி' },
  { value: 'property', label: 'Property', ta: 'சொத்து' },
  { value: 'vehicle', label: 'Vehicle', ta: 'வாகனம்' },
  { value: 'naming', label: 'Naming Ceremony', ta: 'பெயர் வைப்பு' },
  { value: 'thread_ceremony', label: 'Thread Ceremony', ta: 'உபநயனம்' },
  { value: 'house_warming', label: 'House Warming', ta: 'கிரஹப்பிரவேசம்' },
];

const scoreColors = { excellent: '#4caf50', good: '#8bc34a', average: '#ffc107', below_average: '#ff9800', poor: '#f44336' };
const componentColors = {
  tithi: '#6c63ff', nakshatra: '#4caf50', yoga: '#ff9800', karana: '#2196f3', vara: '#e91e63'
};

const langLabels = { en: 'English', ta: 'Tamil' };

/* ─── Component ─── */
const MuhurtaChecker = () => {
  const [lang, setLang] = useState('en');
  const [eventType, setEventType] = useState('marriage');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [hour, setHour] = useState('10');
  const [minute, setMinute] = useState('0');
  const [place, setPlace] = useState('');
  const [lat, setLat] = useState(null);
  const [lon, setLon] = useState(null);
  const [timezone, setTimezone] = useState('Asia/Kolkata');
  const [ayanamsa] = useState('lahiri');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const handlePlaceChange = useCallback((data) => {
    setPlace(data.place);
    setLat(data.lat);
    setLon(data.lon);
    setTimezone(data.timezone);
  }, []);

  const fetchMuhurta = useCallback(async () => {
    if (!lat || !lon) {
      setError('Please select a place from the suggestions to get coordinates.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const dateParts = date.split('-');
      const res = await axios.post('/vedic/v1/muhurta/check', {
        event_type: eventType,
        date,
        year: parseInt(dateParts[0]),
        month: parseInt(dateParts[1]),
        day: parseInt(dateParts[2]),
        hour: parseInt(hour || '0'),
        minute: parseInt(minute || '0'),
        latitude: lat,
        longitude: lon,
        timezone,
        ayanamsa,
        lang,
      });
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to check muhurta');
    } finally {
      setLoading(false);
    }
  }, [eventType, date, hour, minute, lat, lon, timezone, ayanamsa, lang]);

  const getQuality = (score) => {
    if (score >= 80) return 'excellent';
    if (score >= 60) return 'good';
    if (score >= 40) return 'average';
    if (score >= 20) return 'below_average';
    return 'poor';
  };

  const getQualityLabel = (score) => {
    const q = result?.quality || getQuality(score);
    return q.replace('_', ' ');
  };

  const muhurta = result?.muhurta || {};
  const scoreValue = muhurta?.score ?? result?.score ?? result?.total_score ?? 0;
  const quality = muhurta?.quality || result?.quality || getQuality(scoreValue);
  const scoreColor = scoreColors[quality] || '#6c63ff';

  const components = muhurta?.component_scores || result?.components || {
    tithi: muhurta?.tithi_score, nakshatra: muhurta?.nakshatra_score,
    yoga: muhurta?.yoga_score, karana: muhurta?.karana_score, vara: muhurta?.vara_score,
  };
  const panchanga = result?.panchanga || result?.panchanga_details || {};
  const warnings = muhurta?.warnings || result?.warnings || [];
  const doshas = muhurta?.doshas || result?.doshas || [];

  return (
    <PageContainer>
      <PageTitle>{lang === 'ta' ? 'முஹூர்த்தம் சோதனை' : 'Muhurta Checker'}</PageTitle>
      <PageSubtitle>{lang === 'ta' ? 'உங்கள் நிகழ்வுக்கான சிறந்த நேரத்தை சரிபார்க்கவும்' : 'Check auspiciousness of a date & time for your event'}</PageSubtitle>

      <div style={{ textAlign: 'right', marginBottom: 10 }}>
        <Select value={lang} onChange={e => setLang(e.target.value)} style={{ width: 'auto', padding: '6px 10px' }}>
          {Object.entries(langLabels).map(([code, label]) => (
            <option key={code} value={code}>{label}</option>
          ))}
        </Select>
      </div>

      {!result ? (
        <TopGrid>
          <Card>
            <CardTitle>{lang === 'ta' ? 'நிகழ்வு விவரங்கள்' : 'Event Details'}</CardTitle>
            <FormGroup>
              <Label>{lang === 'ta' ? 'நிகழ்வு வகை' : 'Event Type'}</Label>
              <Select name="event_type" value={eventType} onChange={e => setEventType(e.target.value)}>
                {eventTypes.map(et => (
                  <option key={et.value} value={et.value}>
                    {lang === 'ta' ? et.ta : et.label}
                  </option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup>
              <Label>{lang === 'ta' ? 'தேதி' : 'Date'}</Label>
              <Input type="date" value={date} onChange={e => setDate(e.target.value)} required />
            </FormGroup>
            <RowGrid>
              <FormGroup>
                <Label>{lang === 'ta' ? 'மணி' : 'Hour'}</Label>
                <Input type="number" min="0" max="23" value={hour} onChange={e => setHour(e.target.value)} placeholder="0-23" />
              </FormGroup>
              <FormGroup>
                <Label>{lang === 'ta' ? 'நிமிடம்' : 'Minute'}</Label>
                <Input type="number" min="0" max="59" value={minute} onChange={e => setMinute(e.target.value)} placeholder="0-59" />
              </FormGroup>
            </RowGrid>
            <SharedLocationForm
              placeValue={place}
              lat={lat}
              lon={lon}
              timezone={timezone}
              onPlaceChange={handlePlaceChange}
              onSubmit={fetchMuhurta}
              submitLabel={lang === 'ta' ? 'முஹூர்த்தம் சோதி' : 'Check Muhurta'}
              loading={loading}
              error={error}
              pageName="muhurtaChecker"
              lang={lang}
            />
          </Card>
          <Card>
            <CardTitle>{lang === 'ta' ? 'முஹூர்த்தம் என்றால் என்ன?' : 'What is Muhurta?'}</CardTitle>
            <p style={{ color: '#555', lineHeight: 1.7, fontSize: '0.95rem' }}>
              {lang === 'ta'
                ? 'முஹூர்த்தம் என்பது ஒரு குறிப்பிட்ட நிகழ்வைத் தொடங்க மிகவும் சுபமான நேரத்தை தீர்மானிக்கும் வேத ஜோதிட கலை. திதி, நட்சத்திரம், யோகம், கரணம், வாரம் ஆகிய ஐந்து பஞ்சாங்க கூறுகள் பகுப்பாய்வு செய்யப்படுகின்றன.'
                : 'Muhurta is the Vedic astrological art of determining the most auspicious time to begin an activity. Five Panchanga components are analyzed: Tithi (lunar day), Nakshatra (lunar mansion), Yoga (luni-solar combination), Karana (half-tithi), and Vara (weekday).'}
            </p>
            <div style={{ marginTop: 20, display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
              {['Tithi', 'Nakshatra', 'Yoga', 'Karana', 'Vara'].map(name => (
                <span key={name} style={{
                  background: '#f0edff', color: '#6c63ff', padding: '6px 14px',
                  borderRadius: 20, fontSize: '0.8rem', fontWeight: 600
                }}>{name}</span>
              ))}
            </div>
          </Card>
        </TopGrid>
      ) : (
        <>
          <BackButton onClick={() => setResult(null)}>
            {lang === 'ta' ? 'புதிய சோதனை' : 'New Check'}
          </BackButton>

          {/* Score Ring */}
          <Card style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '20px 0' }}>
              <SharedScoreRing value={scoreValue} max={100} color={scoreColor} label={getQualityLabel(scoreValue)} />
            </div>

            {result.summary && (
              <p style={{ textAlign: 'center', color: '#555', fontSize: '1rem', margin: '10px 0 20px' }}>
                {result.summary}
              </p>
            )}

            {/* Component Scores */}
            <ComponentGrid>
              {[
                { key: 'tithi', label: 'Tithi', ta: 'திதி' },
                { key: 'nakshatra', label: 'Nakshatra', ta: 'நட்சத்திரம்' },
                { key: 'yoga', label: 'Yoga', ta: 'யோகம்' },
                { key: 'karana', label: 'Karana', ta: 'கரணம்' },
                { key: 'vara', label: 'Vara', ta: 'வாரம்' },
              ].map(comp => {
                const val = components[comp.key];
                const score = typeof val === 'object' ? (val?.score ?? val?.value ?? '--') : (val ?? '--');
                const color = componentColors[comp.key] || '#6c63ff';
                return (
                  <ScoreCard key={comp.key} label={lang === 'ta' ? comp.ta : comp.label} value={score} color={color} />
                );
              })}
            </ComponentGrid>

            <div style={{ textAlign: 'center' }}>
              <BackButton onClick={() => setResult(null)} style={{ display: 'inline-block' }}>
                {lang === 'ta' ? 'புதிய சோதனை' : 'Check Another'}
              </BackButton>
            </div>
          </Card>

          {/* Panchanga Details */}
          {Object.keys(panchanga).length > 0 && (
            <Card style={{ marginBottom: 20 }}>
              <CardTitle>{lang === 'ta' ? 'பஞ்சாங்க விவரங்கள்' : 'Panchanga Details'}</CardTitle>
              <PanchangaGrid>
                {panchanga.tithi && (
                  <PanchangaCard>
                    <PanchangaLabel>{lang === 'ta' ? 'திதி' : 'Tithi'}</PanchangaLabel>
                    <PanchangaValue>{typeof panchanga.tithi === 'object' ? panchanga.tithi.name : panchanga.tithi}</PanchangaValue>
                    {typeof panchanga.tithi === 'object' && panchanga.tithi.lord && (
                      <PanchangaNote>Lord: {panchanga.tithi.lord}</PanchangaNote>
                    )}
                  </PanchangaCard>
                )}
                {panchanga.nakshatra && (
                  <PanchangaCard>
                    <PanchangaLabel>{lang === 'ta' ? 'நட்சத்திரம்' : 'Nakshatra'}</PanchangaLabel>
                    <PanchangaValue>{typeof panchanga.nakshatra === 'object' ? panchanga.nakshatra.name : panchanga.nakshatra}</PanchangaValue>
                    {typeof panchanga.nakshatra === 'object' && panchanga.nakshatra.pada && (
                      <PanchangaNote>Pada: {panchanga.nakshatra.pada}</PanchangaNote>
                    )}
                  </PanchangaCard>
                )}
                {panchanga.yoga && (
                  <PanchangaCard>
                    <PanchangaLabel>{lang === 'ta' ? 'யோகம்' : 'Yoga'}</PanchangaLabel>
                    <PanchangaValue>{typeof panchanga.yoga === 'object' ? panchanga.yoga.name : panchanga.yoga}</PanchangaValue>
                  </PanchangaCard>
                )}
                {panchanga.karana && (
                  <PanchangaCard>
                    <PanchangaLabel>{lang === 'ta' ? 'கரணம்' : 'Karana'}</PanchangaLabel>
                    <PanchangaValue>{typeof panchanga.karana === 'object' ? panchanga.karana.name : panchanga.karana}</PanchangaValue>
                  </PanchangaCard>
                )}
                {panchanga.vara && (
                  <PanchangaCard>
                    <PanchangaLabel>{lang === 'ta' ? 'வாரம்' : 'Vara (Weekday)'}</PanchangaLabel>
                    <PanchangaValue>{typeof panchanga.vara === 'object' ? panchanga.vara.name : panchanga.vara}</PanchangaValue>
                  </PanchangaCard>
                )}
              </PanchangaGrid>
            </Card>
          )}

          {/* Warnings */}
          {warnings.length > 0 && (
            <Card style={{ marginBottom: 20 }}>
              <CardTitle>{lang === 'ta' ? 'எச்சரிக்கைகள்' : 'Warnings'}</CardTitle>
              {warnings.map((w, i) => (
                <WarningCard key={i}>
                  <WarningText>{typeof w === 'string' ? w : w.message || w.description}</WarningText>
                </WarningCard>
              ))}
            </Card>
          )}

          {/* Doshas */}
          {doshas.length > 0 && (
            <Card>
              <CardTitle>{lang === 'ta' ? 'தோஷங்கள்' : 'Doshas'}</CardTitle>
              {doshas.map((d, i) => (
                <DoshaCard key={i}>
                  <div style={{ fontWeight: 700, color: '#c62828', marginBottom: 4 }}>
                    {typeof d === 'string' ? d : d.name || 'Dosha'}
                  </div>
                  {typeof d === 'object' && d.description && (
                    <WarningText>{d.description}</WarningText>
                  )}
                </DoshaCard>
              ))}
            </Card>
          )}
        </>
      )}
    </PageContainer>
  );
};

export default MuhurtaChecker;
